import { useState, useMemo } from "react";
import {
  Mail, Plus, Edit2, Trash2, Send, X, Eye,
  Users, FileText, BarChart2, Clock, CheckCircle,
  ChevronDown, Layers,
} from "lucide-react";
import { useAdmin } from "../admin-context";
import DataTable from "../DataTable";

// ── Seed data ─────────────────────────────────────────────────────────────────

const SEED = [
  {
    id: 1,
    name: "Welcome Series",
    subject: "Welcome to Gamapa Foods! 🎉",
    recipient_group: "new_customers",
    status: "sent",
    sent_count: 248,
    open_count: 132,
    body: "Hi {{name}},\n\nThank you for joining us! Use code WELCOME20 for 20% off your first order.\n\nSee you soon,\nThe Gamapa Team",
    created_at: "2025-03-10T09:00:00Z",
  },
  {
    id: 2,
    name: "Spring Promotion",
    subject: "🌸 Spring deals are here — don't miss out!",
    recipient_group: "all_customers",
    status: "sent",
    sent_count: 1540,
    open_count: 410,
    body: "Hi {{name}},\n\nSpring has arrived and so have our new seasonal dishes. Order today and enjoy free delivery on orders above $30.\n\nShop now: {{store_url}}",
    created_at: "2025-04-01T08:00:00Z",
  },
  {
    id: 3,
    name: "Loyalty Reward Reminder",
    subject: "You have points waiting! Redeem them now 🏆",
    recipient_group: "loyalty_members",
    status: "draft",
    sent_count: 0,
    open_count: 0,
    body: "Hi {{name}},\n\nYou have {{points}} loyalty points ready to use. Don't let them go to waste!\n\nRedeem at checkout on your next order.",
    created_at: "2025-04-08T14:00:00Z",
  },
];

// ── Constants ─────────────────────────────────────────────────────────────────

const RECIPIENT_GROUPS = [
  { value: "all_customers",   label: "All Customers"     },
  { value: "new_customers",   label: "New Customers"     },
  { value: "loyalty_members", label: "Loyalty Members"   },
  { value: "inactive",        label: "Inactive (30d+)"   },
  { value: "high_spenders",   label: "High Spenders"     },
];

const TEMPLATES = [
  { id: "blank",       label: "Blank",        body: "" },
  { id: "promo",       label: "Promotion",    body: "Hi {{name}},\n\nWe have an exciting offer just for you!\n\n[Describe your promotion here]\n\nShop now: {{store_url}}\n\nBest,\nThe Team" },
  { id: "newsletter",  label: "Newsletter",   body: "Hi {{name}},\n\nHere's what's new this month:\n\n• [Update 1]\n• [Update 2]\n• [Update 3]\n\nThank you for being with us.\n\nBest,\nThe Team" },
  { id: "welcome",     label: "Welcome",      body: "Hi {{name}},\n\nWelcome! We're thrilled to have you.\n\nUse code WELCOME20 for 20% off your first order.\n\nSee you soon,\nThe Team" },
  { id: "reactivation",label: "Re-engagement",body: "Hi {{name}},\n\nWe miss you! It's been a while since your last order.\n\nCome back and enjoy 15% off with code MISSYOU.\n\nSee you soon,\nThe Team" },
];

const STATUS_CHIP = {
  draft:     "bg-gray-100 text-gray-600",
  scheduled: "bg-blue-100 text-blue-700",
  sent:      "bg-green-100 text-green-700",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const openRate = (sent, opened) =>
  sent > 0 ? `${((opened / sent) * 100).toFixed(1)}%` : "—";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const groupLabel = (val) =>
  RECIPIENT_GROUPS.find(g => g.value === val)?.label || val;

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl p-5 flex items-center gap-4 shadow-sm border border-gray-100">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0 ${color}`}>
      <Icon size={20} />
    </div>
    <div>
      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const PreviewModal = ({ campaign, onClose }) => {
  if (!campaign) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-gray-900">Email Preview</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {/* Email mock frame */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 space-y-1">
              <p className="text-xs text-gray-400"><span className="font-semibold text-gray-600">To:</span> customer@example.com</p>
              <p className="text-xs text-gray-400"><span className="font-semibold text-gray-600">Subject:</span> {campaign.subject}</p>
            </div>
            <div className="p-6">
              <div className="bg-orange-500 h-1 w-16 rounded-full mb-4" />
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                {campaign.body}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const EmailCampaignsPage = () => {
  const { showToast, logAudit } = useAdmin();

  const [campaigns, setCampaigns] = useState(SEED);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing]       = useState(null);
  const [preview, setPreview]       = useState(null);
  const [saving, setSaving]         = useState(false);

  const blankForm = () => ({
    name: "", subject: "", recipient_group: "all_customers",
    status: "draft", body: "",
  });

  const [form, setForm] = useState(blankForm());

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openAdd = () => {
    setEditing(null);
    setForm(blankForm());
    setDrawerOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, subject: c.subject, recipient_group: c.recipient_group, status: c.status, body: c.body });
    setDrawerOpen(true);
  };

  const applyTemplate = (tpl) => {
    const t = TEMPLATES.find(t => t.id === tpl);
    if (t) set("body", t.body);
  };

  const handleSave = async (sendNow = false) => {
    if (!form.name.trim() || !form.subject.trim()) {
      showToast("Name and subject are required.", "error");
      return;
    }
    setSaving(true);
    await new Promise(r => setTimeout(r, 400)); // simulate network

    if (editing) {
      setCampaigns(prev => prev.map(c =>
        c.id === editing.id
          ? { ...c, ...form, ...(sendNow ? { status: "sent", sent_count: c.sent_count || Math.floor(Math.random() * 1000 + 100) } : {}) }
          : c
      ));
      logAudit(sendNow ? "Sent" : "Updated", "Email Campaign", form.name);
      showToast(sendNow ? "Campaign sent!" : "Campaign updated.", "success");
    } else {
      const newC = {
        id: Date.now(),
        ...form,
        sent_count: sendNow ? Math.floor(Math.random() * 500 + 50) : 0,
        open_count: 0,
        created_at: new Date().toISOString(),
        ...(sendNow ? { status: "sent" } : {}),
      };
      setCampaigns(prev => [newC, ...prev]);
      logAudit(sendNow ? "Sent" : "Created", "Email Campaign", form.name);
      showToast(sendNow ? "Campaign sent!" : "Campaign saved as draft.", "success");
    }
    setSaving(false);
    setDrawerOpen(false);
  };

  const handleDelete = (id) => {
    const c = campaigns.find(x => x.id === id);
    setCampaigns(prev => prev.filter(x => x.id !== id));
    logAudit("Deleted", "Email Campaign", c?.name || id);
    showToast("Campaign deleted.", "success");
  };

  // — Stats
  const totalSent   = campaigns.filter(c => c.status === "sent").length;
  const totalDraft  = campaigns.filter(c => c.status === "draft").length;
  const totalOpened = campaigns.reduce((s, c) => s + (c.open_count || 0), 0);
  const totalSentMsgs = campaigns.reduce((s, c) => s + (c.sent_count || 0), 0);

  const columns = [
    {
      key: "name", label: "Campaign", sortable: true,
      render: (val, row) => (
        <div>
          <p className="font-semibold text-gray-800 text-sm">{val}</p>
          <p className="text-xs text-gray-400 truncate max-w-[200px]">{row.subject}</p>
        </div>
      ),
    },
    {
      key: "recipient_group", label: "Recipients", sortable: true,
      render: (val) => (
        <span className="flex items-center gap-1.5 text-sm text-gray-600">
          <Users size={13} className="text-gray-400" /> {groupLabel(val)}
        </span>
      ),
    },
    {
      key: "status", label: "Status", sortable: true,
      render: (val) => (
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${STATUS_CHIP[val] || "bg-gray-100 text-gray-600"}`}>
          {val}
        </span>
      ),
    },
    {
      key: "sent_count", label: "Sent", sortable: true,
      render: (val) => <span className="text-sm font-medium text-gray-700">{val?.toLocaleString() || 0}</span>,
    },
    {
      key: "open_count", label: "Open Rate", sortable: false,
      render: (val, row) => (
        <span className="text-sm font-medium text-gray-700">{openRate(row.sent_count, val)}</span>
      ),
    },
    {
      key: "created_at", label: "Date", sortable: true,
      render: (val) => <span className="text-sm text-gray-400">{fmtDate(val)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Mail size={20} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
            <p className="text-xs text-gray-400">Create and send newsletters & promotions</p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm transition"
        >
          <Plus size={16} /> New Campaign
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Layers}      label="Total"       value={campaigns.length}         color="bg-indigo-500" />
        <StatCard icon={CheckCircle} label="Sent"        value={totalSent}                color="bg-green-500"  />
        <StatCard icon={FileText}    label="Drafts"      value={totalDraft}               color="bg-gray-400"   />
        <StatCard icon={BarChart2}   label="Emails Sent" value={totalSentMsgs.toLocaleString()} color="bg-orange-500" />
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={campaigns}
        searchKeys={["name", "subject"]}
        searchPlaceholder="Search campaigns…"
        filters={[
          {
            key: "status",
            label: "Status",
            options: [
              { label: "Draft",     value: "draft"     },
              { label: "Scheduled", value: "scheduled" },
              { label: "Sent",      value: "sent"      },
            ],
          },
        ]}
        defaultPageSize={10}
        emptyText="No campaigns yet. Create your first one!"
        actions={(row) => (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPreview(row)}
              className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition"
              title="Preview"
            >
              <Eye size={14} />
            </button>
            <button
              onClick={() => openEdit(row)}
              className="p-2 rounded-lg border border-orange-200 bg-orange-50 text-orange-600 hover:bg-orange-100 transition"
              title="Edit"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => handleDelete(row.id)}
              className="p-2 rounded-lg border border-red-100 bg-red-50 text-red-500 hover:bg-red-100 transition"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      />

      {/* Preview modal */}
      {preview && <PreviewModal campaign={preview} onClose={() => setPreview(null)} />}

      {/* Create / Edit drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editing ? "Edit Campaign" : "New Campaign"}
      >
        <div className="space-y-5">

          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Campaign Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => set("name", e.target.value)}
              placeholder="e.g. Summer Sale 2025"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition"
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject Line *</label>
            <input
              type="text"
              value={form.subject}
              onChange={e => set("subject", e.target.value)}
              placeholder="e.g. Exclusive offer just for you 🎉"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition"
            />
          </div>

          {/* Recipient group */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Recipients</label>
            <div className="relative">
              <select
                value={form.recipient_group}
                onChange={e => set("recipient_group", e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 appearance-none bg-white transition pr-8"
              >
                {RECIPIENT_GROUPS.map(g => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Template picker */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Load Template</label>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => applyTemplate(t.id)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 transition"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Email Body
              <span className="ml-2 text-gray-300 font-normal">use {"{{name}}"}, {"{{store_url}}"}, {"{{points}}"}</span>
            </label>
            <textarea
              value={form.body}
              onChange={e => set("body", e.target.value)}
              rows={10}
              placeholder="Write your email content here…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition resize-none font-mono"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition"
            >
              {saving ? "Saving…" : "Save Draft"}
            </button>
            <button
              type="button"
              onClick={() => handleSave(true)}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 transition shadow-sm"
            >
              <Send size={15} /> {saving ? "Sending…" : "Send Now"}
            </button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default EmailCampaignsPage;
